/**
 * Presence Socket Service
 *
 * Socket.IO client for presence namespace with typed events.
 * Handles real-time updates for online players list.
 */

import { io, Socket } from 'socket.io-client';
import type { ActivityStatus, OnlinePlayer, InviteNotification, TradeNotification } from '@drawn-of-war/shared';
import { getApiUrl } from '../config/api';
import { getAuthToken } from './auth-token';

/**
 * Detect current activity based on socket connections and page
 */
export function detectCurrentActivity(): ActivityStatus {
  // Check current route
  const pathname = window.location.pathname;

  if (pathname.includes('/battle/') && pathname.includes('/combat')) {
    return 'in_battle';
  }
  if (pathname.includes('/battle/') && pathname.includes('/deploy')) {
    return 'deploying';
  }
  if (pathname.includes('/battles')) {
    return 'in_lobby';
  }
  if (pathname.includes('/create') || pathname.includes('/generate')) {
    return 'creating';
  }

  return 'browsing';
}

/**
 * Presence Socket Client
 *
 * Manages Socket.IO connection to /presence namespace.
 * Handles online player list updates and heartbeat.
 */
export class PresenceSocket {
  private socket: Socket | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly HEARTBEAT_INTERVAL = 30000; // 30 seconds

  // Store callbacks
  private callbacks: {
    onlineList?: (users: OnlinePlayer[]) => void;
    userOnline?: (data: { id: string; displayName: string; activity: ActivityStatus }) => void;
    userOffline?: (data: { id: string }) => void;
    activityChanged?: (data: { id: string; activity: ActivityStatus }) => void;
    userMaybeOffline?: (data: { id: string }) => void;
    // Invite callbacks
    inviteReceived?: (invite: InviteNotification) => void;
    inviteAccepted?: (data: { inviteId: string; by: string; battleId: string }) => void;
    inviteDeclined?: (data: { inviteId: string; by: string }) => void;
    inviteExpired?: (data: { inviteId: string }) => void;
    // Trade callbacks
    tradeOfferReceived?: (trade: TradeNotification) => void;
    tradeCounter?: (trade: TradeNotification) => void;
    tradeCompleted?: (data: { tradeId: string; withUser: string }) => void;
    tradeDeclined?: (data: { tradeId: string; by: string }) => void;
    tradeExpired?: (data: { tradeId: string }) => void;
  } = {};

  /**
   * Connect to the presence namespace
   */
  connect(): void {
    if (this.socket?.connected) {
      return;
    }

    const url = `${getApiUrl()}/presence`;

    this.socket = io(url, {
      auth: async (callback) => {
        try {
          const token = await getAuthToken();
          callback({ token });
        } catch (error) {
          console.error('[PresenceSocket] Failed to get auth token:', error);
          callback({});
        }
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10
    });

    // Register callbacks
    this.registerCallbacks();

    // Auto-subscribe and start heartbeat on connect
    this.socket.on('connect', () => {
      console.log('[PresenceSocket] Connected');
      this.subscribe();
      this.startHeartbeat();
    });

    this.socket.on('disconnect', () => {
      console.log('[PresenceSocket] Disconnected');
      this.stopHeartbeat();
    });

    this.socket.on('connect_error', (error) => {
      console.error('[PresenceSocket] Connection error:', error);
    });
  }

  /**
   * Register event callbacks
   */
  private registerCallbacks(): void {
    if (!this.socket) return;

    if (this.callbacks.onlineList) {
      this.socket.on('presence:online-list', this.callbacks.onlineList);
    }
    if (this.callbacks.userOnline) {
      this.socket.on('presence:user-online', this.callbacks.userOnline);
    }
    if (this.callbacks.userOffline) {
      this.socket.on('presence:user-offline', this.callbacks.userOffline);
    }
    if (this.callbacks.activityChanged) {
      this.socket.on('presence:activity-changed', this.callbacks.activityChanged);
    }
    if (this.callbacks.userMaybeOffline) {
      this.socket.on('presence:user-maybe-offline', this.callbacks.userMaybeOffline);
    }
    // Invite callbacks
    if (this.callbacks.inviteReceived) {
      this.socket.on('invite:received', this.callbacks.inviteReceived);
    }
    if (this.callbacks.inviteAccepted) {
      this.socket.on('invite:accepted', this.callbacks.inviteAccepted);
    }
    if (this.callbacks.inviteDeclined) {
      this.socket.on('invite:declined', this.callbacks.inviteDeclined);
    }
    if (this.callbacks.inviteExpired) {
      this.socket.on('invite:expired', this.callbacks.inviteExpired);
    }
    // Trade callbacks
    if (this.callbacks.tradeOfferReceived) {
      this.socket.on('trade:offer-received', this.callbacks.tradeOfferReceived);
    }
    if (this.callbacks.tradeCounter) {
      this.socket.on('trade:counter', this.callbacks.tradeCounter);
    }
    if (this.callbacks.tradeCompleted) {
      this.socket.on('trade:completed', this.callbacks.tradeCompleted);
    }
    if (this.callbacks.tradeDeclined) {
      this.socket.on('trade:declined', this.callbacks.tradeDeclined);
    }
    if (this.callbacks.tradeExpired) {
      this.socket.on('trade:expired', this.callbacks.tradeExpired);
    }
  }

  /**
   * Disconnect from presence namespace
   */
  disconnect(): void {
    this.stopHeartbeat();
    if (this.socket) {
      this.unsubscribe();
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Subscribe to online player updates
   */
  subscribe(): void {
    if (this.socket) {
      this.socket.emit('presence:subscribe');
    }
  }

  /**
   * Unsubscribe from updates
   */
  unsubscribe(): void {
    if (this.socket) {
      this.socket.emit('presence:unsubscribe');
    }
  }

  /**
   * Start heartbeat interval
   */
  private startHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    // Send immediate heartbeat
    this.sendHeartbeat();

    // Then every 30 seconds
    this.heartbeatInterval = setInterval(() => {
      this.sendHeartbeat();
    }, this.HEARTBEAT_INTERVAL);

    // Also send heartbeat on visibility change (returning to tab)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Stop heartbeat interval
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /**
   * Handle visibility change
   */
  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.sendHeartbeat();
    }
  };

  /**
   * Send heartbeat with current activity
   */
  sendHeartbeat(): void {
    if (this.socket?.connected) {
      const activity = detectCurrentActivity();
      this.socket.emit('presence:heartbeat', { activity });
    }
  }

  /**
   * Set hidden status (privacy toggle)
   */
  setHidden(hidden: boolean): void {
    if (this.socket) {
      this.socket.emit('presence:set-hidden', { hidden });
    }
  }

  /**
   * Listen for online player list updates
   */
  onOnlineList(callback: (users: OnlinePlayer[]) => void): void {
    this.callbacks.onlineList = callback;
    if (this.socket) {
      this.socket.on('presence:online-list', callback);
    }
  }

  /**
   * Listen for user online events
   */
  onUserOnline(callback: (data: { id: string; displayName: string; activity: ActivityStatus }) => void): void {
    this.callbacks.userOnline = callback;
    if (this.socket) {
      this.socket.on('presence:user-online', callback);
    }
  }

  /**
   * Listen for user offline events
   */
  onUserOffline(callback: (data: { id: string }) => void): void {
    this.callbacks.userOffline = callback;
    if (this.socket) {
      this.socket.on('presence:user-offline', callback);
    }
  }

  /**
   * Listen for activity change events
   */
  onActivityChanged(callback: (data: { id: string; activity: ActivityStatus }) => void): void {
    this.callbacks.activityChanged = callback;
    if (this.socket) {
      this.socket.on('presence:activity-changed', callback);
    }
  }

  /**
   * Listen for user maybe offline events
   */
  onUserMaybeOffline(callback: (data: { id: string }) => void): void {
    this.callbacks.userMaybeOffline = callback;
    if (this.socket) {
      this.socket.on('presence:user-maybe-offline', callback);
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Listen for invite received events
   */
  onInviteReceived(callback: (invite: InviteNotification) => void): void {
    this.callbacks.inviteReceived = callback;
    if (this.socket) {
      this.socket.on('invite:received', callback);
    }
  }

  /**
   * Listen for invite accepted events
   */
  onInviteAccepted(callback: (data: { inviteId: string; by: string; battleId: string }) => void): void {
    this.callbacks.inviteAccepted = callback;
    if (this.socket) {
      this.socket.on('invite:accepted', callback);
    }
  }

  /**
   * Listen for invite declined events
   */
  onInviteDeclined(callback: (data: { inviteId: string; by: string }) => void): void {
    this.callbacks.inviteDeclined = callback;
    if (this.socket) {
      this.socket.on('invite:declined', callback);
    }
  }

  /**
   * Listen for invite expired events
   */
  onInviteExpired(callback: (data: { inviteId: string }) => void): void {
    this.callbacks.inviteExpired = callback;
    if (this.socket) {
      this.socket.on('invite:expired', callback);
    }
  }

  /**
   * Remove invite listeners
   */
  offInviteListeners(): void {
    if (this.socket) {
      this.socket.off('invite:received');
      this.socket.off('invite:accepted');
      this.socket.off('invite:declined');
      this.socket.off('invite:expired');
    }
    this.callbacks.inviteReceived = undefined;
    this.callbacks.inviteAccepted = undefined;
    this.callbacks.inviteDeclined = undefined;
    this.callbacks.inviteExpired = undefined;
  }

  /**
   * Listen for trade offer received events
   */
  onTradeOfferReceived(callback: (trade: TradeNotification) => void): void {
    this.callbacks.tradeOfferReceived = callback;
    if (this.socket) {
      this.socket.on('trade:offer-received', callback);
    }
  }

  /**
   * Listen for trade counter events
   */
  onTradeCounter(callback: (trade: TradeNotification) => void): void {
    this.callbacks.tradeCounter = callback;
    if (this.socket) {
      this.socket.on('trade:counter', callback);
    }
  }

  /**
   * Listen for trade completed events
   */
  onTradeCompleted(callback: (data: { tradeId: string; withUser: string }) => void): void {
    this.callbacks.tradeCompleted = callback;
    if (this.socket) {
      this.socket.on('trade:completed', callback);
    }
  }

  /**
   * Listen for trade declined events
   */
  onTradeDeclined(callback: (data: { tradeId: string; by: string }) => void): void {
    this.callbacks.tradeDeclined = callback;
    if (this.socket) {
      this.socket.on('trade:declined', callback);
    }
  }

  /**
   * Listen for trade expired events
   */
  onTradeExpired(callback: (data: { tradeId: string }) => void): void {
    this.callbacks.tradeExpired = callback;
    if (this.socket) {
      this.socket.on('trade:expired', callback);
    }
  }

  /**
   * Remove trade listeners
   */
  offTradeListeners(): void {
    if (this.socket) {
      this.socket.off('trade:offer-received');
      this.socket.off('trade:counter');
      this.socket.off('trade:completed');
      this.socket.off('trade:declined');
      this.socket.off('trade:expired');
    }
    this.callbacks.tradeOfferReceived = undefined;
    this.callbacks.tradeCounter = undefined;
    this.callbacks.tradeCompleted = undefined;
    this.callbacks.tradeDeclined = undefined;
    this.callbacks.tradeExpired = undefined;
  }
}

// --- Singleton factory with reference counting ---

let sharedInstance: PresenceSocket | null = null;
let refCount = 0;

/**
 * Get the shared PresenceSocket singleton.
 * Connects on first consumer, subsequent calls return the same instance.
 * Call releaseSharedPresenceSocket() when done (e.g. on component unmount).
 */
export function getSharedPresenceSocket(): PresenceSocket {
  if (!sharedInstance) {
    sharedInstance = new PresenceSocket();
    sharedInstance.connect();
  }
  refCount++;
  return sharedInstance;
}

/**
 * Release a reference to the shared PresenceSocket.
 * Disconnects and destroys the singleton when the last consumer releases.
 */
export function releaseSharedPresenceSocket(): void {
  refCount--;
  if (refCount <= 0) {
    refCount = 0;
    if (sharedInstance) {
      sharedInstance.disconnect();
      sharedInstance = null;
    }
  }
}
